import { AlertTriangle, X } from 'lucide-react';
import { useState } from 'react';
import { useEVMTransaction } from '../../../../hooks/useEvmTransaction';

import { classifyTransaction } from '../../../../lib/evm/transaction';
import { createDeclineTransactionMessage } from '../../../../lib/transaction-messages';
import { shortenString } from '../../../../lib/utils';

import { CardFooter, CardHeader } from '../../../ui/card';
import { CopyStandard } from '../../CopyStandard';
import DefaultTxApproveButton from '../../default-components/DefaultTxApproveButton';
import DefaultTxContainer from '../../default-components/DefaultTxContainer';
import DefaultTxDeclineButton from '../../default-components/DefaultTxDeclineButton';
import LoadingMessage from '../../loading/LoadingMessage';
import { TransactionDetail } from '../TransactionDetail';
import { TransactionResult } from '../TransactionResult';
import { EvmApprovalCardProps } from './types';

export const EvmApprovalCard = ({
  evmData,
  approvalInfo,
  messageBackgroundColor,
  borderColor,
  textColor,
  customTxContainer: TxContainer = DefaultTxContainer,
  customApproveTxButton: ApproveButton = DefaultTxApproveButton,
  customDeclineTxButton: DeclineButton = DefaultTxDeclineButton,
  append,
  messageId,
  classification: providedClassification,
}: EvmApprovalCardProps) => {
  const {
    isLoading,
    errorMsg,
    transaction,
    handleSmartAction,
    network,
    evmAddress,
  } = useEVMTransaction({ evmData, append, messageId });

  const [isDeclined, setIsDeclined] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  // Use provided classification or compute it
  const classification = providedClassification ?? classifyTransaction(evmData);

  const declineTransaction = async () => {
    setIsDeclining(true);
    try {
      await append(createDeclineTransactionMessage(messageId));
      setIsDeclined(true);
    } catch (error) {
      console.error('Error declining transaction:', error);
    } finally {
      setIsDeclining(false);
    }
  };

  if (isDeclined) {
    return (
      <TxContainer
        style={{
          backgroundColor: messageBackgroundColor,
          borderColor,
          textColor,
        }}
      >
        <div className="bitte-p-6 bitte-text-center">
          <X className="bitte-mx-auto bitte-mb-2 bitte-h-8 bitte-w-8 bitte-text-red-500" />
          <p>Transaction declined</p>
        </div>
      </TxContainer>
    );
  }

  if (transaction && transaction.txHash) {
    return (
      <TransactionResult
        result={{
          evm: { txHash: transaction.txHash, chainId: evmData.chainId },
        }}
        accountId={evmAddress || ''}
        textColor={textColor}
        success={transaction.status === 'success'}
      />
    );
  }

  const loadingIndicator = (
    <div>
      <LoadingMessage color={textColor} />
      <p className="bitte-text-center bitte-text-sm">
        {isDeclining
          ? 'Declining approval...'
          : `Approval for ${shortenString(approvalInfo.spenderAddress, 8)} in progress`}
      </p>
    </div>
  );

  if (isLoading || isDeclining) {
    return (
      <TxContainer
        style={{
          backgroundColor: messageBackgroundColor,
          borderColor,
          textColor,
        }}
      >
        <div className="bitte-p-6">{loadingIndicator}</div>
      </TxContainer>
    );
  }

  return (
    <TxContainer
      style={{
        backgroundColor: messageBackgroundColor,
        borderColor,
        textColor,
      }}
    >
      <CardHeader className="bitte-p-4 bitte-text-center bitte-md:p-6">
        <div className="bitte-flex bitte-items-center bitte-justify-center bitte-gap-2">
          <p className="bitte-text-xl bitte-font-semibold">
            {classification.title}
          </p>
        </div>
        <p className="bitte-mt-2 bitte-text-xs bitte-opacity-80">
          You are approving a contract to spend your tokens.
          <br />
          This transaction is required before the contract can transfer tokens
          on your behalf for swaps or other operations.
        </p>
      </CardHeader>

      <div className="bitte-p-6">
        <div className="bitte-flex bitte-flex-col bitte-gap-6 bitte-text-sm">
          <TransactionDetail
            label="Network"
            value={
              <div className="bitte-flex bitte-items-center bitte-gap-2">
                {network?.icon && (
                  <img
                    src={network.icon}
                    alt={network.name}
                    className="bitte-h-4 bitte-w-4"
                  />
                )}
                <span>{network?.name || ''}</span>
              </div>
            }
          />

          <TransactionDetail
            label="Token Contract"
            value={
              <div className="bitte-flex bitte-items-center bitte-gap-2">
                <CopyStandard text={approvalInfo.tokenAddress} textSize="sm" />
              </div>
            }
          />

          <TransactionDetail
            label="Spender"
            value={
              <div className="bitte-flex bitte-items-center bitte-gap-2">
                <CopyStandard
                  text={approvalInfo.spenderAddress}
                  textSize="sm"
                />
              </div>
            }
          />

          {approvalInfo.suspicious && (
            <div className="bitte-rounded-lg bitte-bg-red-500/10 bitte-border bitte-border-gray-700 bitte-p-3">
              <div className="bitte-flex bitte-items-center bitte-gap-2 bitte-text-red-500">
                <AlertTriangle className="bitte-h-4 bitte-w-4" />
                <span className="bitte-font-medium">Suspicious Activity</span>
              </div>
              <p className="bitte-mt-1 bitte-text-xs bitte-text-red-400">
                This approval contains unusual parameters. Review carefully
                before proceeding.
              </p>
            </div>
          )}
        </div>
      </div>

      {errorMsg && (
        <div className="bitte-p-4">
          <div className="bitte-rounded-lg bitte-bg-red-500/10 bitte-border bitte-border-gray-700 bitte-p-3">
            <p className="bitte-text-red-500 bitte-text-sm">{errorMsg}</p>
          </div>
        </div>
      )}

      <CardFooter className="bitte-flex bitte-gap-3 bitte-p-4">
        <DeclineButton
          onClick={declineTransaction}
          isLoading={isDeclining}
          disabled={isLoading}
          label="Decline"
        />
        <ApproveButton
          onClick={handleSmartAction}
          isLoading={isLoading}
          disabled={isDeclining}
          label="Approve"
        />
      </CardFooter>
    </TxContainer>
  );
};
